import {
  DownloadOutlined,
  EditOutlined,
} from '@ant-design/icons';
import {
  Breadcrumb,
  Button,
  Checkbox,
  Col,
  Divider,
  Dropdown,
  Input,
  Menu,
  message,
  Modal,
  Row,
  Space,
  Tooltip,
  Form,
  Upload,
  Spin,
} from 'antd';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import 'jspdf-autotable';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ReactComponent as Back } from '../../Images/Back.svg';
import { ReactComponent as ColumnList } from '../../Images/ColumnList.svg';
import dayjs from 'dayjs';
import {
  fetchWorkOrderById,
  fetchViewWorkOrderByCloseOrderId,
  createPriority,
} from '../../slices/work-order';
import PrintWorkOrder3 from '../Authentication/PrintWorkOrder3';
import html2pdf from 'html2pdf.js';
import { LoadingOutlined } from '@ant-design/icons';
import WarningModal from '../../Common/WarningModal';

const ViewWorkOrderList = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const { id } = useParams();
  const dispatch = useDispatch();
  const { user: currentUser } = useSelector(state => state.auth);
  const [btnLoading, setBtnLoading] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [loadingPDF, setLoadingPDF] = useState(false);
  const navigate = useNavigate();
  const [workOrderData, setWorkOrderData] = useState(null);
  const [checkedList, setCheckedList] = useState([
    'SrNo',
    'die_profile',
    'profile_image',
    'profile_description',
    'weight_range',
    'length',
    'pieces',
    'net_weight',
    'packed_weight',
    'dispatched_weight',
    'pending_weight',
    'alloy',
  ]);

  const printRef = useRef();
  const [closeOrderModalVisible, setCloseOrderModalVisible] = useState(false);
  const [closeOrderRemark, setCloseOrderRemark] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  const [popupData, setPopupData] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [closingDocFile, setClosingDocFile] = useState(null);
  const [open, setOpen] = useState(false);
  function getFileExtension(url) {
    return url.split('.').pop().split(/[?#]/)[0];
  }

  const showModal = async (data, title) => {
    let extension = getFileExtension(data);
    const isDwg = extension === 'dwg';
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(
      extension,
    );
    if (isImage) {
      setModalTitle(title);
      setPopupData(data);
      setIsModalVisible(true);
    } else if (isDwg) {
      window.open(data, '_blank', 'noopener,noreferrer');
    } else {
      const response = await fetch(data);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const blobData = await response.blob();
      var blob = new Blob([blobData], { type: 'application/pdf' });
      var blobURL = URL.createObjectURL(blob);
      window.open(blobURL);
    }
  };

  const loadImageAsBase64 = (url) => {
    return new Promise((resolve, reject) => {
      const cacheBustUrl = `${url}${url.includes('?') ? '&' : '?'}nocache=${new Date().getTime()}`;

      const img = new Image();
      img.crossOrigin = 'Anonymous';

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = cacheBustUrl;
    });
  };

  const handleDownloadPDF = () => {
    const element = printRef.current;
    if (!element) return;

    setLoadingPDF(true);
    const clone = element.cloneNode(true);
    clone.querySelectorAll('.no-print').forEach(el => el.remove());
    const logo = clone.querySelector('img.company-logo');
    if (logo) {
      const logoWrapper = document.createElement('div');
      logoWrapper.style.backgroundColor = 'white';
      logoWrapper.style.display = 'inline-block';
      logoWrapper.style.padding = '6px';
      logoWrapper.style.borderRadius = '4px';
      logoWrapper.style.textAlign = 'center';

      logo.parentNode.insertBefore(logoWrapper, logo);
      logoWrapper.appendChild(logo);
    }
    const imageElements = Array.from(clone.querySelectorAll('img')).filter(img => {
      const src = img.getAttribute('src') || '';
      return !img.classList.contains('company-logo') && !src.includes('logo.png');
    });

    const style = document.createElement('style');
    style.innerHTML = `
    * {
        box-sizing: border-box !important;
        margin: 0 !important;
        padding: 0 !important;
        color: #000000 !important;
        font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji !important;
      }
      @page {
        size: A4 portrait;
        margin: 10mm !important;
      }
      body {
        font-size: 9px !important;
        margin: 0 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
      .print-container-workorder {
        width: 794px !important;
        min-height: 1000px !important;
        position: relative !important;
        padding-bottom: 100px !important;
        margin: 0 auto !important;
      }
      .main-heading {
        font-size: 15px !important;
        text-align: center !important;
        width: 100% !important;
      }
      .page {
        page-break-after: always !important;
        position: relative !important;
        box-sizing: border-box !important;
        overflow: visible !important;
        width: 794px !important;
      }
      .header-text {
        font-size: 10px !important;
        line-height: 1.2 !important;
        text-align: left !important;
      }
      .page-content {
        width: 100% !important;
        overflow: visible !important;
        font-size: 10px !important;
      }
      .signature-section {
        position: absolute !important;
        bottom: 10px !important;
        width: 100% !important;
        text-align: center !important;
        margin-top: 0 !important;
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }
      .ant-row {
        display: flex !important;
        flex-wrap: wrap !important;
        margin-bottom: 5px !important;
      }
      .ant-col {
        box-sizing: border-box !important;
        padding: 1px !important;
      }
      h1, h2, h3, h4, h5 {
        color: rgb(2, 15, 28) !important;
        font-size: 12px !important;
        margin-bottom: 3px !important;
        margin-top: 0 !important;
      }
      .ant-table {
        width: 794px !important;
        font-size: 9px !important;
        table-layout: fixed !important;
        margin: 5px 0 !important;
        border-collapse: collapse !important;
      }
      .ant-table-container {
        overflow: visible !important;
        border: 1px solid #dee2e6 !important;
        padding: 0px !important;
        width: 794px !important;
      }
      .ant-table-wrapper {
        padding: 0 !important;
        overflow: visible !important;
        width: 794px !important;
      }
      .ant-table-thead > tr > th,
      .ant-table-tbody > tr > td {
        padding: 2px 2px !important;
        font-size: 8px !important;
        background-color: white !important;
        border: 1px solid #dee2e6 !important;
        white-space: normal !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        word-break: break-word !important;
        line-height: 1.1 !important;
        height: auto !important;
        min-height: 18px !important;
      }
      .ant-table-thead > tr > th {
        background: #fff !important;
        padding: 2px !important;
        font-weight: bold !important;
      }
      .ant-table-wrapper .ant-table-summary > tr > td {
        border: 1px solid #dee2e6 !important;
        padding: 1px 1px !important;
        font-size: 8px !important;
      }
      .summary-cell-center {
        text-align: center !important;
      }
      .summary-cell-right {
        text-align: right !important;
        padding-right: 3px !important;
      }
      .ant-divider {
        margin: 5px 0 !important;
        border-top: 1px solid #d9d9d9 !important;
      }
      .no-print {
        display: none !important;
      }
      .info-section {
        display: flex;
        justify-content: space-between;
        margin: 20px 0 !important;
        font-size: 12px !important;
      }
      .info-left {
        flex: 1;
        max-width: 60%;
      }
      .info-right {
        flex: 1;
        text-align: right;
        max-width: 35%;
      }
      .info-table {
        border-collapse: collapse;
        width: 100%;
        font-size: 12px;
      }
      .info-table td {
        vertical-align: top;
        padding: 2px 0;
      }
      .info-label {
        font-weight: bold;
      }
      .main-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 12px !important;
        margin-bottom: 20px;
      }
      .main-table th,
      .main-table td {
        border: 1px solid #dee2e6;
        padding: 6px 4px;
        text-align: center;
        vertical-align: middle;
        font-size: 12px;
      }
      .main-table th {
        background: transparent;
        color: black;
        font-weight: bold;
        font-size: 12px;
      }
      .main-table .section-image {
        width: 60px;
        height: 60px;
        object-fit: contain;
      }
      .total-row {
        font-weight: bold;
      }
      .signature-section {
        display: flex;
        justify-content: space-between;
        margin: 40px 0 20px;
        gap: 60px;
      }
      .signature-box {
        text-align: center;
        font-weight: bold;
        font-size: 12px;
      }
      .logo-section img {
        max-width: 150px !important;
        height: auto !important;
        position: absolute !important;
        top: 0 !important;
        right: 0 !important;
      }
      .copy-label {
        float: right;
        font-size: 14px;
        font-weight: normal;
      }
      .new-info {
        margin-top: 20px !important;
      }`;
    clone.appendChild(style);
    const base64ConversionPromises = imageElements.map(img => {
      const src = img.getAttribute('src');
      if (!src || src.startsWith('data:') || img.classList.contains('company-logo')) {
        return Promise.resolve();
      }

      return loadImageAsBase64(src)
        .then(base64 => {
          img.setAttribute('src', base64);
        })
        .catch(err => {
          console.warn('Failed to load base64 image:', src, err);
        });
    });
    const logoImage = clone.querySelector('img.company-logo');
    if (logoImage && !logoImage.src.startsWith('data:')) {
      base64ConversionPromises.push(
        loadImageAsBase64(logoImage.src)
          .then(base64 => {
            logoImage.setAttribute('src', base64);
          })
          .catch(err => {
            console.warn('Failed to load base64 logo:', logoImage.src, err);
          })
      );
    }
    Promise.all(base64ConversionPromises)
      .then(() => {
        setTimeout(() => {
          const opt = {
            margin: 0,
            filename: `WorkOrder_${workOrderData?.order_no || 'N/A'}.pdf`,
            image: { type: 'png', quality: 1 },
            html2canvas: {
              scale: 2,
              useCORS: true,
              allowTaint: true,
              logging: true,
              imageTimeout: 15000,
            },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
          };

          html2pdf().set(opt).from(clone).save()
            .then(() => setLoadingPDF(false))
            .catch(err => {
              console.error("PDF generation error:", err);
              setLoadingPDF(false);
            });
        }, 500);
      })
      .catch(err => {
        console.error("Error converting images to base64:", err);
        setLoadingPDF(false);
      });
  };

  const fetchData = useCallback(async () => {
    try {
      const response = await dispatch(
        fetchWorkOrderById(id)
      ).unwrap();

      setWorkOrderData(response?.data);
    } catch (error) {
      console.error('Failed to fetch quotation:', error);
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id, fetchData]);

  const handleCloseOrder = async () => {
    setCloseOrderModalVisible(true);
  };

  const handleCloseOrderSubmit = async () => {
    setBtnLoading(true);
    try {
      const formData = new FormData();
      formData.append('reason_to_close', closeOrderRemark);
      if (closingDocFile) {
        formData.append('wo_closing_doc', closingDocFile);
      }

      await dispatch(
        fetchViewWorkOrderByCloseOrderId({
          id,
          formData,
        }),
      ).unwrap();

      setBtnLoading(false);
      setCloseOrderModalVisible(false);
      setCloseOrderRemark('');
      setClosingDocFile(null);
      message.success('Work Order Closed Successfully!');
      setWorkOrderData(prevData => ({
        ...prevData,
        status: 'Closed',
      }));

      fetchData();
    } catch (error) {
      setBtnLoading(false);
      console.error('Failed to close work order:', error);
    }
  };

  const handleCloseOrderCancel = () => {
    setCloseOrderModalVisible(false);
    setCloseOrderRemark('');
  };

  const handlePriority = () => {
    const formdata = {
      workorder_detail_ids: selectedRowKeys,
    };
    dispatch(createPriority({ values: formdata }))
      .unwrap()
      .then(result => {
        setBtnLoading(false);
        if (result?.added_to_priority > 0) {
          //message.success(result?.message);
          const msgs = result?.success_messages;

          if (Array.isArray(msgs) && msgs.length > 0) {
            const successrMessage = msgs.join("\n"); // combine into one message

            message.success({
              content: (
                <div style={{ whiteSpace: "pre-line" }}>
                  {successrMessage}
                </div>
              ),
              duration: 4,
            });
          }
        }
        if (result?.skipped_count > 0) {
          const msgs = result?.skipped_messages;

          if (Array.isArray(msgs) && msgs.length > 0) {
            const errorMessage = msgs.join("\n"); // combine into one message

            message.error({
              content: (
                <div style={{ whiteSpace: "pre-line" }}>
                  {errorMessage}
                </div>
              ),
              duration: 4,
            });
          }
        }
      })
      .catch(error => {
        setBtnLoading(false);
        const messages = [];
        if (typeof error === 'object' && error !== null) {
          for (const key in error) {
            if (Array.isArray(error[key])) {
              messages.push(`${key}: ${error[key].join(', ')}`);
            }
          }
        }
        if (messages.length > 0) {
          message.error(messages.join('; '));
        } else if (typeof error === 'string') {
          message.error(error);
        } else {
          message.error('An unknown error occurred.');
        }
      });
  };

  const handleColumnVisibilityChange = checkedValues => {
    setCheckedList(checkedValues);
  };
  const columnMenu = (
    <Menu className="packagelistdropdown">
      <Menu.Item key="columnMenu">
        <Checkbox.Group
          value={checkedList}
          onChange={checkedValues => {
            handleColumnVisibilityChange(checkedValues);
          }}
        >
          <Row gutter={16}>
            <Col xs={12} md={12}>
              <Checkbox value="SrNo" disabled>
                Sr.No.
              </Checkbox>
            </Col>
            <Col xs={12} md={12}>
              <Checkbox value="die_profile">Profile Number</Checkbox>
            </Col>
            <Col xs={12} md={12}>
              <Checkbox value="profile_image">Profile Image</Checkbox>
            </Col>
            <Col xs={12} md={12}>
              <Checkbox value="profile_description">
                Profile Description
              </Checkbox>
            </Col>
            <Col xs={12} md={12}>
              <Checkbox value="weight_range">Weight Range</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="length">Length(mm)</Checkbox>
            </Col>
            <Col xs={12} md={12}>
              <Checkbox value="pieces">Pieces</Checkbox>
            </Col>
            <Col xs={12} md={12}>
              <Checkbox value="net_weight">Net Weight</Checkbox>
            </Col>
            <Col xs={12} md={12}>
              <Checkbox value="packed_weight">Packed Weight</Checkbox>
            </Col>
            <Col xs={12} md={12}>
              <Checkbox value="dispatched_weight">Dispatched Weight</Checkbox>
            </Col>
            <Col xs={12} md={12}>
              <Checkbox value="pending_weight">Pending Weight</Checkbox>
            </Col>
            <Col xs={12} md={12}>
              <Checkbox value="alloy">Alloy/Temp</Checkbox>
            </Col>
          </Row>
        </Checkbox.Group>
      </Menu.Item>
    </Menu>
  );

  const columns = [
    {
      title: 'Sr.No',
      dataIndex: 'SrNo',
      render: (text, record, index) => index + 1,
      align: 'center',
    },
    {
      title: 'Profile Number',
      dataIndex: 'die_profile',
      align: 'center',
      key: 'die_profile',
      render: (text, record) => {
        return <span>{record?.die_profile?.die_number || 'N/A'}</span>;
      },
    },
    {
      title: 'Profile Image',
      dataIndex: 'profile_image',
      key: 'profile_image',
      render: (text, record) => {
        if (!record?.die_profile?.die_diagram) {
          return <span>N/A</span>;
        }

        const fileUrl = record?.die_profile?.die_diagram;
        const isImage = /\.(jpeg|jpg|png|gif|bmp)$/i.test(fileUrl);
        const isPDF = /\.pdf$/i.test(fileUrl);

        return isImage ? (
          <img
            src={fileUrl}
            alt="Die Diagram"
            style={{
              width: 90,
              height: 90,
              objectFit: 'contain',
              borderRadius: 4,
              maxHeight: '100%',
            }}
          />
        ) : isPDF ? (
          <a href={fileUrl} target="_blank" rel="noopener noreferrer">
            View PDF
          </a>
        ) : (
          <span>Unsupported Format</span>
        );
      },
    },
    {
      title: 'Profile Description (mm)',
      dataIndex: 'profile_description',
      key: 'profile_description',

      render: (text, record) => {
        const dimensions = [
          record?.die_profile?.dimension1,
          record?.die_profile?.dimension2,
          record?.die_profile?.dimension3,
          record?.die_profile?.dimension4,
        ]
          .filter(
            dim =>
              dim !== null && dim !== '' && dim !== undefined && dim !== '0.00',
          )
          .map(dim => Number(dim).toFixed(2))
          .join(' X ');

        return <span>{dimensions || 'N/A'}</span>;
      },
    },
    {
      title: 'Weight Range/Piece(kg)',
      dataIndex: 'weight_range',
      key: 'weight_range',
      align: 'center',
      render: weight_range => {
        return <span>{weight_range || 'N/A'}</span>;
      },
    },
    {
      title: 'Length(mm)',
      dataIndex: 'length',
      key: 'length',
      align: 'center',
      render: length => {
        return <span>{length || 'N/A'}</span>;
      },
    },
    {
      title: 'Pieces',
      dataIndex: 'pieces',
      key: 'pieces',
      align: 'center',
      render: pieces => {
        return <span>{pieces || 'N/A'}</span>;
      },
    },
    {
      title: 'Net Weight(kg)',
      dataIndex: 'net_weight',
      key: 'net_weight',
      align: 'right',
      render: net_weight => {
        return <span>{net_weight || 'N/A'}</span>;
      },
    },
    {
      title: 'Packed Weight(kg)',
      dataIndex: 'packed_weight',
      key: 'packed_weight',
      align: 'right',
      render: packed_weight => {
        return <span>{packed_weight || '0'}</span>;
      },
    },
    {
      title: 'Dispatched Weight(kg)',
      dataIndex: 'dispatched_weight',
      key: 'dispatched_weight',
      align: 'right',
      render: dispatched_weight => {
        return <span>{dispatched_weight || '0'}</span>;
      },
    },
    {
      title: 'Pending Weight(kg)',
      dataIndex: 'pending_weight',
      key: 'pending_weight',
      align: 'right',
      render: pending_weight => {
        return <span>{pending_weight || '0'}</span>;
      },
    },
    {
      title: 'Alloy/Temper',
      dataIndex: 'alloy',
      key: 'alloy',
      render: (_, record) => {
        const alloyName = record?.alloy?.alloy_code || 'N/A';
        const alloyCode = record?.alloy?.standard_name || 'N/A';
        const temperName = record?.temper?.temper_code_new || 'N/A';
        const temperCode = record?.temper?.temper_code_old || 'N/A';
        return (
          <span>
            {alloyName} ({alloyCode}) / {temperName} ({temperCode})
          </span>
        );
      },
    },
  ];

  const dynamicColumns = columns.filter(column =>
    checkedList.includes(column.dataIndex || column.key),
  );

  const toleranceOptions = {
    'Zero(0)': 'Zero(0)',
    '+-3%': '±3%',
    '+-5%': '±5%',
    '+-7%': '±7%',
    '+-10%': '±10%',
    '+3%': '+3%',
    '+5%': '+5%',
    '+7%': '+7%',
    '+10%': '+10%',
    '-3%': '-3%',
    '-5%': '-5%',
    '-7%': '-7%',
    '-10%': '-10%',
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setPopupData(null);
    setModalTitle('');
  };

  return (
    <>
      <style>
        {`
    /* Table container */
    .ant-table {
      font-size: 16px !important;
      background: #fff !important;
      color: #1d2129 !important;
      border-radius: 8px !important;
      box-shadow: 0 2px 8px rgba(0,0,0,0.04) !important;
      overflow: hidden !important;
    }

    /* Table header */
    .ant-table-thead > tr > th {
      background: #12406a !important; /* Ant blue-9 */
      color: #fff !important;
      font-weight: 600 !important;
      font-size: 16px !important;
      border-bottom: 2px solid #e6f4ff !important;
      padding: 10px 12px !important;
      letter-spacing: 0.5px;
    }

    /* Table body rows */
    .ant-table-tbody > tr > td {
      background: #12406a !important; /* Ant gray-1 */
      color: #1d2129 !important;
      font-size: 14px !important;
      border-bottom: 1px solid #e6f4ff !important;
      padding: 10px 12px !important;
      transition: background 0.2s;
    }

    /* Zebra striping for rows */
    .ant-table-tbody > tr:nth-child(odd) > td {
      background: #fff !important;
    }

    /* Row hover */
    .ant-table-tbody > tr:hover > td {
      background: #12406a !important; /* Ant blue-1 */
    }

    /* Table summary/footer */
    .ant-table-summary > tr > td {
      background: #12406a !important; /* Ant blue-2 */
      color: #12406a !important;
      font-weight: 600 !important;
      font-size: 15px !important;
      border-top: 2px solid #bae0ff !important;
      padding: 10px 12px !important;
    }

    /* Checkbox column background */
    .ant-table-tbody > tr > td.ant-table-selection-column,
    .ant-table-thead > tr > th.ant-table-selection-column {
      background: #12406a !important;
    }

    /* Responsive border radius for first/last cells */
    .ant-table-thead > tr > th:first-child,
    .ant-table-tbody > tr > td:first-child,
    .ant-table-summary > tr > td:first-child {
      border-top-left-radius: 8px !important;
      border-bottom-left-radius: 8px !important;
    }
    .ant-table-thead > tr > th:last-child,
    .ant-table-tbody > tr > td:last-child,
    .ant-table-summary > tr > td:last-child {
      border-top-right-radius: 8px !important;
      border-bottom-right-radius: 8px !important;
    }
  `}
      </style>
      {loadingPDF && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Spin
            indicator={
              <LoadingOutlined style={{ fontSize: 48, color: '#12406a' }} spin />
            }
            tip="Generating PDF..."
          />
        </div>
      )}
      <div>
        <div
          className=""
          style={{
            position: 'sticky',
            zIndex: '1000',
            background: '#fff',
            padding: '16px',
            top: '0',
          }}
        >
          <Row
            className="pagenamerow mb-0"
            justify="space-between"
            align="middle"
          >
            <Col>
              <h2 style={{ margin: 0 }}>View WorkOrder</h2>
              <div className="bredcrumbwrp">
                <Link onClick={() => navigate(-1)} className="back">
                  <Back /> BACK
                </Link>
                <Breadcrumb
                  items={[
                    { title: <Link to="/dashboard">Home</Link> },
                    { title: <Link to="/work-order-list">WorkOrders</Link> },
                    { title: 'View WorkOrder' },
                  ]}
                />
              </div>
            </Col>
            <Col>
              {currentUser.permissions.permissions.workorder.includes(
                'change_workorder',
              ) && (
                  <>
                    <Tooltip placement="top" title={'Download PDF'}>
                      <Button
                        type="primary"
                        icon={loadingPDF ? <LoadingOutlined /> : <DownloadOutlined />}
                        style={{ marginBottom: '20px', float: 'right' }}
                        onClick={handleDownloadPDF}
                        loading={loadingPDF}
                      >
                        {loadingPDF ? 'Generating PDF...' : 'Download PDF'}
                      </Button>
                    </Tooltip>
                    <div style={{ display: 'none' }}>
                      <div ref={printRef} style={{ width: '800px' }}>
                        <PrintWorkOrder3 />
                      </div>
                    </div>
                  </>
                )}
              {currentUser.permissions.permissions.workorder.includes(
                'change_workorder',
              ) && (
                  <Button
                    type="primary"
                    onClick={() =>
                      navigate(`/edit-work-order/${workOrderData?.id}`)
                    }
                    style={{
                      marginBottom: '20px',
                      float: 'center',
                      marginRight: '10px',
                    }}
                    icon={<EditOutlined />}
                  >
                    Edit WorkOrder
                  </Button>
                )}
              {workOrderData?.status !== 'Closed' && (
                <Button
                  type="primary"
                  htmlType="submit"
                  style={{
                    marginBottom: '20px',
                    float: 'center',
                    marginRight: '10px',
                  }}
                  onClick={handleCloseOrder}
                  loading={btnLoading}
                >
                  Close Order
                </Button>
              )}
            </Col>
          </Row>
        </div>
        <div className="formwrapper">
          <Row gutter={[16, 16]} className="viewform">
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Order Date</label>
              <div className="customvalue">
                {workOrderData?.order_date
                  ? dayjs(workOrderData.order_date).format('DD/MM/YYYY')
                  : 'N/A'}
              </div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Order Number</label>
              <div className="customvalue">
                <div className="customvalue">
                  {workOrderData?.order_no || 'N/A'}
                </div>
              </div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">PO No.</label>
              <div className="customvalue">
                {workOrderData?.purchase_order_no || 'N/A'}
              </div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Customer (Bill To)</label>
              <div className="customvalue">
                {workOrderData?.bill_to?.customer_name || 'N/A'}
              </div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Customer (Ship To)</label>
              <div className="customvalue">
                {workOrderData?.ship_to?.customer_name || 'N/A'}
              </div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Mode Of Packing</label>
              <div className="customvalue">
                {workOrderData?.packing_mode_details?.length
                  ? workOrderData.packing_mode_details.map(item => item.name).join(', ')
                  : 'N/A'}
              </div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Project Name</label>
              <div className="customvalue">
                {workOrderData?.project_name || 'N/A'}
              </div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">PO Date</label>
              <div className="customvalue">
                {dayjs(workOrderData?.purchase_order_date).format(
                  'DD/MM/YYYY',
                ) || 'N/A'}
              </div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Tolerance</label>
              <div className="customvalue">
                <span>
                  {workOrderData?.tolerance
                    ? toleranceOptions[workOrderData.tolerance]
                    : 'N/A'}
                </span>
              </div>
            </Col>

            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Nalco Rate</label>
              <div className="customvalue">
                {workOrderData?.nalco_type || 'N/A'}
              </div>
            </Col>

            {workOrderData?.nalco_type === "Fixed" && (
              <Col xs={24} sm={12} lg={8} xl={6}>
                <label className="customlabel">Nalco Rate Approved By</label>
                <div className="customvalue">
                  {`${workOrderData?.approved_by_detail?.first_name}  ${workOrderData?.approved_by_detail?.last_name}` || 'N/A'}
                </div>
              </Col>
            )}
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Total Weight</label>
              <div className="customvalue">
                {workOrderData?.total_weight
                  ? `${Number(workOrderData.total_weight).toFixed(3)} kg`
                  : '0.00 kg'}
              </div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Packed Weight</label>
              <div className="customvalue">
                {workOrderData?.total_packed_weight
                  ? `${Number(workOrderData.total_packed_weight).toFixed(3)} kg`
                  : '0.00 kg'}
              </div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Dispatched Weight</label>
              <div className="customvalue">
                {workOrderData?.total_dispatched_weight
                  ? `${Number(workOrderData.total_dispatched_weight).toFixed(3)} kg`
                  : '0.00 kg'}
              </div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Pending Weight </label>
              <div className="customvalue">
                {workOrderData?.total_pending_weight
                  ? `${Number(workOrderData.total_pending_weight).toFixed(3)} kg`
                  : '0.00 kg'}
              </div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Delivery Date</label>
              <div className="customvalue">
                {workOrderData?.delivery_date
                  ? dayjs(workOrderData.delivery_date).format('DD/MM/YYYY')
                  : 'N/A'}
              </div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Status</label>
              <div className="customvalue">
                {workOrderData?.status || 'N/A'}
              </div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Remarks</label>
              <div className="customvalue">
                {workOrderData?.remarks || 'N/A'}
              </div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label
                htmlFor="viewFileButton"
                style={{ display: 'block', marginBottom: '8px' }}
              >
                Purchase Order Copy:
              </label>
              {workOrderData?.po_copy !== null ? (
                <Button
                  type="secondry"
                  onClick={() =>
                    showModal(workOrderData?.po_copy, 'Purchase Order Copy')
                  }
                >
                  View File
                </Button>
              ) : (
                <div className="customvalue">N/A</div>
              )}
            </Col>

            <Col xs={24} sm={12} lg={8} xl={6}>
              <label
                htmlFor="viewFileButton"
                style={{ display: 'block', marginBottom: '8px' }}
              >
                Closing Remark Document
              </label>
              {workOrderData?.wo_closing_doc !== null ? (
                <Button
                  type="secondry"
                  onClick={() =>
                    showModal(
                      workOrderData?.wo_closing_doc,
                      'Closing Remark Document',
                    )
                  }
                >
                  View File
                </Button>
              ) : (
                <div className="customvalue">N/A</div>
              )}
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Close Remarks </label>
              <div className="customvalue" style={{ color: 'red' }}>
                {workOrderData?.reason_to_close || 'N/A'}
              </div>
            </Col>
          </Row>

          <Divider />

          <div className="flex justify-between gridtitle mt-10" >
            <h3>WorkOrder Items List</h3>
            <Space size="small">
              <Tooltip
                placement="top"
                title={'Move the selected Item in Priority'}
              >
                <Button
                  type="primary"
                  style={{ float: 'right' }}
                  onClick={handlePriority}
                  disabled={selectedRowKeys.length === 0}
                >
                  Priority
                </Button>
              </Tooltip>

              <Tooltip placement="topLeft" title="Column Filter Options">
                <Dropdown
                  overlay={columnMenu}
                  trigger={['click']}
                  placement="bottomRight"
                  visible={dropdownVisible}
                  onVisibleChange={setDropdownVisible}
                >
                  <Button
                    type="primary"
                    icon={<ColumnList />}
                    onClick={e => e.preventDefault()}
                  />
                </Dropdown>
              </Tooltip>
            </Space>
          </div>

          <div className="table-container">
            <div className="table-header">
              <div className="selected-info">
                {selectedRowKeys.length > 0 && (
                  <span>{selectedRowKeys.length} items selected</span>
                )}
              </div>
            </div>
            <WarningModal
              open={open}
              onClose={() => setOpen(false)}
              message="This work order item is already in process, so it cannot be added to priority."
            />
            <table className="simple-table">
              <thead>
                <tr>
                  <th>
                    <Checkbox
                      onChange={e => {
                        const allKeys = workOrderData?.work_order_details
                          .filter(item => !item.is_priority)
                          .map(item => item.id);
                        setSelectedRowKeys(e.target.checked ? allKeys : []);
                      }}
                      checked={
                        selectedRowKeys.length > 0 &&
                        selectedRowKeys.length ===
                        workOrderData?.work_order_details.filter(
                          item => !item.is_priority,
                        ).length
                      }
                      indeterminate={
                        selectedRowKeys.length > 0 &&
                        selectedRowKeys.length <
                        workOrderData?.work_order_details.filter(
                          item => !item.is_priority,
                        ).length
                      }
                    />
                  </th>
                  {dynamicColumns.map(column => (
                    <th
                      key={column.key || column.dataIndex}
                      data-column={column.dataIndex || column.key}
                    >
                      {column.title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {workOrderData?.work_order_details?.map((record, index) => (
                  <tr key={record.id}>
                    <td>
                      <Checkbox
                        // checked={selectedRowKeys.includes(record.id)}
                        onChange={e => {
                          if (record?.status === "In-Process") {
                            setOpen(true);
                            return false;
                          }
                          if (e.target.checked) {
                            setSelectedRowKeys([...selectedRowKeys, record.id]);
                          } else {
                            setSelectedRowKeys(
                              selectedRowKeys.filter(key => key !== record.id),
                            );
                          }
                        }}
                        disabled={record.is_priority}
                        checked={
                          record.is_priority ||
                          selectedRowKeys.includes(record.id)
                        }
                      />
                    </td>
                    {dynamicColumns.map(column => (
                      <td
                        key={column.key || column.dataIndex}
                        data-column={column.dataIndex || column.key}
                      >
                        {column.render
                          ? column.render(
                            record[column.dataIndex],
                            record,
                            index,
                          )
                          : record[column.dataIndex]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>
                    Total
                  </td>
                  {dynamicColumns.map((column, index) => {
                    const columnKey = column.dataIndex || column.key;
                    const totals = {
                      pieces: workOrderData?.work_order_details?.reduce(
                        (sum, item) => sum + (parseFloat(item.pieces) || 0),
                        0,
                      ),
                      net_weight: workOrderData?.work_order_details?.reduce(
                        (sum, item) => sum + (parseFloat(item.net_weight) || 0),
                        0,
                      ),
                      packed_weight: workOrderData?.work_order_details?.reduce(
                        (sum, item) =>
                          sum + (parseFloat(item.packed_weight) || 0),
                        0,
                      ),
                      dispatched_weight:
                        workOrderData?.work_order_details?.reduce(
                          (sum, item) =>
                            sum + (parseFloat(item.dispatched_weight) || 0),
                          0,
                        ),
                      pending_weight: workOrderData?.work_order_details?.reduce(
                        (sum, item) =>
                          sum + (parseFloat(item.pending_weight) || 0),
                        0,
                      ),
                    };

                    if (index === 0) {
                      return <td key={columnKey}></td>;
                    } else if (totals[columnKey]) {
                      return (
                        <td key={columnKey} data-column={columnKey}>
                          {columnKey === 'packed_weight'
                            ? totals[columnKey].toFixed(3)
                            : totals[columnKey].toLocaleString('en-IN')}
                        </td>
                      );
                    } else {
                      return <td key={columnKey}></td>;
                    }
                  })}
                </tr>
              </tfoot>
            </table>
          </div>

          <style>
            {`
              .table-container {
                width: 100%;
                overflow-x: auto;
                margin-top: 20px;
              }

              .table-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 16px;
                padding: 8px 0;
              }

              .selected-info {
                font-size: 14px;
                color: #666;
              }

              .selected-info span {
                background-color: #f0f0f0;
                padding: 4px 8px;
                border-radius: 4px;
                margin-right: 8px;
              }

              .simple-table {
                width: 100%;
                border-collapse: collapse;
                font-size: 14px;
              }

              .simple-table th,
              .simple-table td {
                padding: 12px;
                text-align: left;
                border: 1px solid #e8e8e8;
              }

              /* Center alignment for specific columns */
              .simple-table th[data-column="pieces"],
              .simple-table td[data-column="pieces"],
              .simple-table th[data-column="length"],
              .simple-table td[data-column="length"],
              .simple-table th[data-column="die_profile"],
              .simple-table td[data-column="die_profile"],
              .simple-table th[data-column="profile_image"],
              .simple-table td[data-column="profile_image"] {
                text-align: center;
              }

              /* Right alignment for weight columns */
              .simple-table th[data-column="net_weight"],
              .simple-table td[data-column="net_weight"],
              .simple-table th[data-column="packed_weight"],
              .simple-table td[data-column="packed_weight"],
              .simple-table th[data-column="dispatched_weight"],
              .simple-table td[data-column="dispatched_weight"],
              .simple-table th[data-column="pending_weight"],
              .simple-table td[data-column="pending_weight"] {
                text-align: right;
              }

              .simple-table th {
                font-weight: 500;
                color: #262626;
              }

              .simple-table tbody tr:hover {
                background-color: #fafafa;
              }

              .simple-table tfoot tr {
                font-weight: bold;
              }

              .simple-table img {
                max-width: 90px;
                max-height: 90px;
                object-fit: contain;
                border-radius: 4px;
              }

              .ant-select {
                min-width: 120px;
              }

              .ant-select-disabled {
                background-color: #f5f5f5;
              }
            `}
          </style>
        </div>{' '}
      </div>

      {/* Close Order Modal */}
      <Modal
        title="Close Work Order"
        open={closeOrderModalVisible}
        onOk={handleCloseOrderSubmit}
        onCancel={handleCloseOrderCancel}
        confirmLoading={btnLoading}
      >
        <Form layout="vertical">
          <Form.Item
            label="Remarks"
            required
            rules={[{ required: true, message: 'Please enter remarks' }]}
          >
            <Input.TextArea
              rows={4}
              value={closeOrderRemark}
              onChange={e => setCloseOrderRemark(e.target.value)}
              placeholder="Enter remarks for closing the work order"
            />
          </Form.Item>
          <Form.Item label="Upload Closing Document">
            <Upload
              beforeUpload={file => {
                const isLt2M = file.size / 1024 / 1024 < 2;
                if (!isLt2M) {
                  message.error('File must be smaller than 2MB!');
                  return Upload.LIST_IGNORE;
                }
                setClosingDocFile(file);
                return false; // Prevent auto upload
              }}
              fileList={closingDocFile ? [closingDocFile] : []}
              onRemove={() => setClosingDocFile(null)}
              accept=".pdf,.jpg,.jpeg,.png"
              maxCount={1}
            >
              <Button>Click to Upload</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      {/* Existing Modal */}
      <Modal
        title={modalTitle}
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={800}
      >
        {popupData ? (
          <img src={popupData} alt="" style={{ width: '100%', height: 'auto' }} />
        ) : (
          <p>No diagram available.</p>
        )}
      </Modal>
    </>
  );
};

export default ViewWorkOrderList;
